import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard, ExternalLink, Users } from 'lucide-react';
import { SUBSCRIPTION_STATUS_LABEL, type PlanDto, type SeatMode } from '@mg/shared';
import { api, ApiRequestError } from '../lib/api';
import { PageHeader } from '../components/shell/PageHeader';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Select } from '../components/ui/Field';
import { Input } from '../components/ui/Field';
import { AlertBanner } from '../components/ui/AlertBanner';
import { ErrorState, LoadingState } from '../components/ui/States';
import { EDGE_BLEED_FRAME, useEdgeBleed } from '../components/ui/useEdgeBleed';
import { formatDateLong, formatDateTimeLong } from '../lib/display';
import { cn } from '../lib/cn';
import { planPriceLabel } from '../features/billing/planPrice';

/** "25th", "1st" — a billing day reads as a date, not a bare number. */
function ordinal(day: number): string {
  const rem100 = day % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
}

/** Plan prices are whole dollars; invoices are NOT — see formatInvoiceAmount. */
/**
 * Invoice amounts render to the CENT. Reusing the whole-dollar plan formatter here would round a
 * real prorated or taxed invoice and disagree with the customer's Stripe receipt.
 */
function formatInvoiceAmount(cents: number, currency: string): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function BillingPage() {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const summaryQuery = useQuery({
    queryKey: ['billing'],
    queryFn: ({ signal }) => api.getBilling(signal),
  });
  const plansQuery = useQuery({
    queryKey: ['billing-plans'],
    queryFn: ({ signal }) => api.getBillingPlans(signal),
  });
  const membersQuery = useQuery({
    queryKey: ['billing-members'],
    queryFn: ({ signal }) => api.getBillingMembers(signal),
  });
  const invoicesQuery = useQuery({
    queryKey: ['billing-invoices'],
    queryFn: ({ signal }) => api.getBillingInvoices(signal),
  });

  const summary = summaryQuery.data;
  const sub = summary?.subscription ?? null;
  // The SERVER decides; the page only reflects it. Every member can read this page — plan, seats
  // and invoices are not secrets from the staff — but only the Billing Admin may change anything.
  const canManage = Boolean(summary?.canManage);

  // Returning from Stripe: refresh, then drop the marker so a reload doesn't repeat the message.
  useEffect(() => {
    const checkout = params.get('checkout');
    if (!checkout) return;
    if (checkout === 'success') {
      setNotice(
        'Payment received. Your subscription is being activated — this can take a few seconds.',
      );
      void queryClient.invalidateQueries({ queryKey: ['billing'] });
      void queryClient.invalidateQueries({ queryKey: ['billing-invoices'] });
    } else {
      setNotice('Checkout was cancelled. Nothing has been charged.');
    }
    const next = new URLSearchParams(params);
    next.delete('checkout');
    setParams(next, { replace: true });
  }, [params, queryClient, setParams]);

  const fail = (err: unknown) =>
    setError(
      err instanceof ApiRequestError ? err.message : 'Something went wrong. Please try again.',
    );

  const choosePlan = useMutation({
    mutationFn: (planId: string) => api.startCheckout(planId),
    onSuccess: (result) => {
      setError(null);
      // A live subscription is repointed in place and returns no URL; only a first subscription
      // sends the user to Stripe.
      if (result.url) {
        window.location.assign(result.url);
        return;
      }
      setNotice(result.message ?? 'Your plan was updated.');
      void queryClient.invalidateQueries({ queryKey: ['billing'] });
      void queryClient.invalidateQueries({ queryKey: ['billing-plans'] });
    },
    onError: fail,
  });

  const portal = useMutation({
    mutationFn: () => api.openBillingPortal(),
    onSuccess: ({ url }) => window.location.assign(url),
    onError: fail,
  });

  const [seatMode, setSeatMode] = useState<SeatMode>('METERED');
  const [seatCap, setSeatCap] = useState('');
  // Hydrate ONCE. Re-syncing on every `summary` change would overwrite what the admin is typing the
  // moment any refetch landed — a background refresh, or the invalidate fired by another mutation.
  const seatHydrated = useRef(false);
  useEffect(() => {
    if (!summary || seatHydrated.current) return;
    seatHydrated.current = true;
    setSeatMode(summary.seats.seatMode);
    setSeatCap(summary.seats.cap != null ? String(summary.seats.cap) : '');
  }, [summary]);

  const saveSeatPolicy = useMutation({
    mutationFn: () =>
      api.setSeatPolicy({
        mode: seatMode,
        cap: seatMode === 'CAP' ? Number.parseInt(seatCap, 10) : null,
      }),
    onSuccess: (next) => {
      setError(null);
      setNotice('Seat policy saved.');
      queryClient.setQueryData(['billing'], next);
    },
    onError: fail,
  });

  // On a desktop screen too narrow for the billing history, its card runs off the page's right edge
  // and the table reaches through the card's padding to that edge, so the cut says it scrolls.
  const invoicesBleedRef = useEdgeBleed<HTMLDivElement>('marked');

  // No self-gate: READING billing is open to every member — what the organisation is on, how many
  // seats it uses and what it has been invoiced is not a secret from the people it employs. The
  // API enforces that same split (reads open, writes Billing-Admin-only) and sends `canManage`, so
  // this page shows everyone the state and only the Billing Admin the controls. A Tenant-Admin gate
  // here also stranded a Billing Admin who is not a Tenant Admin — the one person expected to fix
  // payment — on a refusal page.

  if (summaryQuery.isLoading) return <LoadingState />;
  if (summaryQuery.isError) {
    return (
      <ErrorState
        message="Billing could not be loaded."
        onRetry={() => void summaryQuery.refetch()}
      />
    );
  }

  const plans = plansQuery.data ?? [];
  const invoices = invoicesQuery.data ?? [];
  const members = membersQuery.data ?? [];

  return (
    <div>
      <PageHeader
        icon={CreditCard}
        title="Billing"
        description="Your plan, seats, payment method and invoices."
      />

      {notice && (
        <div className="mb-4">
          <AlertBanner tone="info">{notice}</AlertBanner>
        </div>
      )}
      {error && (
        <div className="mb-4">
          <AlertBanner tone="warning">{error}</AlertBanner>
        </div>
      )}

      {!summary?.billingConfigured && (
        <div className="mb-4">
          <AlertBanner tone="warning">
            Billing isn&apos;t set up for this installation yet. Add your Stripe keys
            (STRIPE_SECRET_KEY and STRIPE_WEBHOOK_SECRET) to enable checkout.
          </AlertBanner>
        </div>
      )}

      {!canManage && (
        <div className="mb-4">
          <AlertBanner tone="info">
            You have read-only access to billing. A Tenant Admin can designate a Billing Admin in
            App Settings.
          </AlertBanner>
        </div>
      )}

      {sub?.suspendedForNonPayment && (
        <div className="mb-4">
          <AlertBanner tone="warning">
            This account is suspended for non-payment. Update your payment method to restore access.
          </AlertBanner>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Current plan + seats */}
        <Card className="lg:col-span-2">
          <CardBody>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-ink">Current plan</h2>
                <p className="mt-1 text-2xl font-bold text-ink">
                  {sub?.planName ?? 'No plan selected'}
                </p>
                {sub && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Badge
                      tone={
                        sub.status === 'ACTIVE'
                          ? 'green'
                          : sub.status === 'PAST_DUE'
                            ? 'amber'
                            : 'neutral'
                      }
                      size="sm"
                    >
                      {SUBSCRIPTION_STATUS_LABEL[sub.status]}
                    </Badge>
                    {sub.currentPeriodEnd && (
                      <span className="text-xs text-ink-muted">
                        {sub.cancelAtPeriodEnd ? 'Ends' : 'Renews'}{' '}
                        {formatDateTimeLong(sub.currentPeriodEnd)}
                      </span>
                    )}
                  </div>
                )}
                {sub && (sub.startedAt || sub.currentPeriodEnd) && (
                  <dl className="mt-3 flex flex-wrap gap-x-8 gap-y-1 text-xs">
                    {sub.startedAt && (
                      <div>
                        <dt className="text-ink-faint">Plan started</dt>
                        <dd className="font-medium text-ink-strong">{formatDateLong(sub.startedAt)}</dd>
                      </div>
                    )}
                    {sub.currentPeriodEnd && (
                      <div>
                        <dt className="text-ink-faint">Billing cycle</dt>
                        {/* Taken from the renewal date IN THE READER'S timezone, so it can never
                            disagree with the "Renews …" line printed right above it. Deriving the
                            day server-side put it in UTC, which reads a day late for anyone west of
                            it — a renewal at 21:06 on the 12th was billed as "the 13th". */}
                        <dd className="font-medium text-ink-strong">
                          {ordinal(new Date(sub.currentPeriodEnd).getDate())} of each month
                        </dd>
                      </div>
                    )}
                  </dl>
                )}
              </div>
              {summary?.billingConfigured && (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={portal.isPending}
                  onClick={() => portal.mutate()}
                >
                  Manage billing
                  <ExternalLink className="h-4 w-4" aria-hidden />
                </Button>
              )}
            </div>

            <div className="mt-5 flex items-center gap-2 border-t border-line-soft pt-4 text-sm text-ink-label">
              <Users className="h-4 w-4 text-ink-faint" aria-hidden />
              <span>
                <strong className="text-ink">{summary?.seats.members ?? 0}</strong> seats in use
                {summary?.seats.cap != null && <> · capped at {summary.seats.cap}</>}
              </span>
            </div>

            {members.length > 0 && (
              <ul className="mt-3 max-h-40 space-y-1 overflow-y-auto text-xs text-ink-muted">
                {members.map((m) => (
                  <li key={m.email} className="flex items-center justify-between gap-2">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate">{m.name ?? m.email}</span>
                      {/* Same green as App Settings, so the holder reads identically in both. */}
                      {m.isBillingAdmin && (
                        <Badge tone="green" size="sm">
                          Billing Admin
                        </Badge>
                      )}
                    </span>
                    <span className="shrink-0 text-ink-faint">{m.role}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        {/* Payment method */}
        <Card>
          <CardHeader title="Payment method" />
          <CardBody>
            {summary?.card ? (
              <p className="text-sm text-ink-strong">
                <span className="capitalize">{summary.card.brand}</span> ····{summary.card.last4}
                <span className="ml-2 text-xs text-ink-faint">
                  exp {summary.card.expMonth}/{summary.card.expYear}
                </span>
              </p>
            ) : (
              <p className="text-sm text-ink-faint">No card on file.</p>
            )}
            {/* This card had no control at all, so a tester could not add or edit a card: the only
                portal link sat in the subscription card above and read "Manage billing". The action
                belongs where the thing it changes is shown. */}
            {canManage && summary?.billingConfigured && (
              <div className="mt-3">
                {summary.hasBillingAccount ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={portal.isPending}
                    onClick={() => portal.mutate()}
                  >
                    {summary.card ? 'Update payment method' : 'Add payment method'}
                    <ExternalLink className="h-4 w-4" aria-hidden />
                  </Button>
                ) : (
                  // Stripe has no customer for this install yet, so its portal cannot be opened.
                  // Saying why beats a button that answers 400.
                  <p className="text-xs text-ink-faint">
                    Select a plan first — your billing account is created when you do, and a card
                    can be added from then on.
                  </p>
                )}
              </div>
            )}
            {summary?.billingAddress && (
              <address className="mt-3 not-italic text-xs leading-relaxed text-ink-muted">
                {[
                  summary.billingAddress.line1,
                  summary.billingAddress.line2,
                  [summary.billingAddress.city, summary.billingAddress.state]
                    .filter(Boolean)
                    .join(', '),
                  summary.billingAddress.postalCode,
                  summary.billingAddress.country,
                ]
                  .filter(Boolean)
                  .map((line) => (
                    <div key={String(line)}>{line}</div>
                  ))}
              </address>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Plans */}
      <Card className="mt-4">
        <CardHeader title="Plans" />
        <CardBody>
          <div className="grid gap-3 md:grid-cols-3">
            {plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                current={sub?.planId === plan.id}
                busy={choosePlan.isPending}
                canManage={canManage}
                onSelect={() => choosePlan.mutate(plan.id)}
              />
            ))}
            {plans.length === 0 && (
              <p className="text-sm text-ink-faint">
                No plans available yet. Alloterra sets these up for your installation.
              </p>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Seat policy */}
      <Card className="mt-4">
        <CardHeader
          title="Seat policy"
          description="Seats grow automatically as you add people. Set a cap to block new users past a limit — you are always billed for actual users, never the cap."
        />
        <CardBody>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-48">
              <Select
                aria-label="Seat mode"
                value={seatMode}
                onChange={(e) => setSeatMode(e.target.value as SeatMode)}
              >
                <option value="METERED">Grow automatically</option>
                <option value="CAP">Cap the number of seats</option>
              </Select>
            </div>
            {seatMode === 'CAP' && (
              <div className="w-32">
                <Input
                  type="number"
                  min={1}
                  aria-label="Seat cap"
                  value={seatCap}
                  onChange={(e) => setSeatCap(e.target.value)}
                  placeholder="e.g. 25"
                />
              </div>
            )}
            <Button
              size="sm"
              loading={saveSeatPolicy.isPending}
              onClick={() => saveSeatPolicy.mutate()}
            >
              Save
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Invoices */}
      <Card {...EDGE_BLEED_FRAME} className="mt-4">
        <CardHeader title="Billing history" />
        <CardBody>
          {invoices.length === 0 && !summary?.upcomingInvoice ? (
            <p className="text-sm text-ink-faint">No invoices yet.</p>
          ) : (
            <div ref={invoicesBleedRef} className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line-soft text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    <th className="py-2 pr-3">Invoice</th>
                    <th className="py-2 pr-3">Date</th>
                    <th className="py-2 pr-3">Amount</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {summary?.upcomingInvoice && (
                    <tr className="border-b border-line-soft bg-surface-sunken">
                      <td className="py-2 pr-3">
                        <Badge tone="neutral" size="sm">
                          Upcoming
                        </Badge>
                      </td>
                      <td className="whitespace-nowrap py-2 pr-3 text-ink-label">
                        {summary.upcomingInvoice.nextAttemptAt
                          ? formatDateTimeLong(summary.upcomingInvoice.nextAttemptAt)
                          : '—'}
                      </td>
                      <td className="whitespace-nowrap py-2 pr-3 font-medium text-ink">
                        {formatInvoiceAmount(
                          summary.upcomingInvoice.amountDue,
                          summary.upcomingInvoice.currency,
                        )}
                      </td>
                      <td className="py-2 pr-3 text-ink-label">
                        Projected
                        {summary.upcomingInvoice.quantity != null && (
                          <span className="ml-1 text-xs text-ink-faint">
                            · {summary.upcomingInvoice.quantity} seat
                            {summary.upcomingInvoice.quantity === 1 ? '' : 's'}
                          </span>
                        )}
                      </td>
                      <td className="py-2" />
                    </tr>
                  )}
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-b border-line-soft last:border-b-0">
                      <td className="py-2 pr-3 text-ink-label">{inv.number ?? '—'}</td>
                      <td className="whitespace-nowrap py-2 pr-3 text-ink-label">
                        {formatDateTimeLong(inv.createdAt)}
                      </td>
                      <td className="whitespace-nowrap py-2 pr-3 text-ink">
                        {formatInvoiceAmount(inv.total, inv.currency)}
                      </td>
                      <td className="py-2 pr-3 capitalize text-ink-label">{inv.status ?? '—'}</td>
                      <td className="py-2 text-right">
                        {inv.hostedInvoiceUrl && (
                          <a
                            href={inv.hostedInvoiceUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs font-medium text-link hover:text-action-hover hover:underline"
                          >
                            View
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function PlanCard({
  plan,
  current,
  busy,
  canManage,
  onSelect,
}: {
  plan: PlanDto;
  current: boolean;
  busy: boolean;
  canManage: boolean;
  onSelect: () => void;
}) {
  const unsynced = !plan.stripePriceId;
  return (
    <div
      className={cn(
        'rounded-lg border p-4',
        current ? 'border-action bg-tint/40' : 'border-line bg-card',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-ink">{plan.name}</h3>
        {current && (
          <Badge tone="green" size="sm">
            Current
          </Badge>
        )}
      </div>
      <p className="mt-1 text-xl font-bold text-ink">
        {planPriceLabel(plan)}
        <span className="ml-1 text-xs font-normal text-ink-muted">
          / user / {plan.billingInterval === 'YEAR' ? 'year' : 'month'}
        </span>
      </p>
      {plan.description && <p className="mt-2 text-xs text-ink-muted">{plan.description}</p>}
      <Button
        className="mt-3 w-full"
        size="sm"
        variant={current ? 'secondary' : 'primary'}
        disabled={busy || current || unsynced || !canManage}
        onClick={onSelect}
      >
        {current ? 'Current plan' : unsynced ? 'Not synced' : 'Select plan'}
      </Button>
      {unsynced && (
        <p className="mt-1 text-[0.6875rem] text-ink-faint">Create this product in Stripe, then sync.</p>
      )}
    </div>
  );
}
